import React from "react";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Input } from "antd5";
import classNames from "classnames";

import TextButton from "components/actions/TextButton";
import Button from "components/ui/button/Button";
import Badge from "lib/core_components/Badge";
import MultiSelectDropdown from "lib/core_components/MultiSelectDropdownList";
import { PaginationState } from "lib/search/types";
import { EventNames, TrackingProvider, useTracking } from "lib/tracking";
import { JobFunction } from "lib/utils/oscarDataUtils";
import ContactsFilterDrawer from "./ContactsFilterDrawer";
import {
  CATEGORY_TYPES,
  contactFilterCount,
  ContactFilters,
  CONTACTS_DISPLAY,
  SENIORITY_OPTIONS,
} from "./contactUtils";
import ExportContactsButton from "./ExportContactsButton";

import css from "./ContactsFilterBar.module.scss";

type Props = {
  contactFilters: ContactFilters;
  selectedRowGuids: string[];
  totalResults: number;
  onChangeSearchFilters: (filters: Partial<ContactFilters>) => void;
  defaultFilters: ContactFilters;
  showDropdownFilters: boolean;
  showAllFiltersDrawer: boolean;
  hideBuyerFilters?: boolean; // If this prop is provided, then contacts are searched for already within thr context of a buyer
  paginationState: PaginationState;
};

export function ContactsFilterBar({
  contactFilters,
  selectedRowGuids,
  totalResults,
  onChangeSearchFilters,
  defaultFilters,
  showDropdownFilters,
  hideBuyerFilters,
  showAllFiltersDrawer,
  paginationState,
}: Props) {
  const [textSearch, setTextSearch] = React.useState(contactFilters.jobTitle || "");

  const [filterDrawerVisible, setFilterDrawerVisible] = React.useState<boolean>(false);

  const { logEvent } = useTracking();

  const handleClearFilters = () => {
    onChangeSearchFilters({
      ...defaultFilters,
    });
    setTextSearch(defaultFilters.jobTitle);
    logEvent(EventNames.filterActioned, {
      "Filter name": "All filters",
      "Filter selection": "All options",
      "Action type": "Filter cleared",
    });
  };

  const resultsCount = selectedRowGuids.length > 0 ? selectedRowGuids.length : totalResults;

  const handleTextSearch = (value: string) => {
    setTextSearch(value);
    onChangeSearchFilters({ jobTitle: value });
    logEvent(EventNames.filterActioned, {
      "Filter name": "Job title",
      "Filter selection": value,
      "Action type": value.length > 0 ? "Filter applied" : "Filter cleared",
    });
  };

  const handleChangeFilters = (filters: Partial<ContactFilters>) => {
    if (filters.jobTitle !== textSearch) {
      setTextSearch(filters.jobTitle || "");
    }
    onChangeSearchFilters(filters);
  };

  const filtersAppliedCount = React.useMemo(() => {
    return contactFilterCount(contactFilters, defaultFilters);
  }, [contactFilters, defaultFilters]);

  return (
    <>
      <TrackingProvider data={{ "Context source": "Contacts filter slide-out" }}>
        <ContactsFilterDrawer
          isOpen={filterDrawerVisible}
          onChangeSearchFilters={handleChangeFilters}
          onClose={() => setFilterDrawerVisible(false)}
          clearFilters={handleClearFilters}
          contactFilters={contactFilters}
        />
      </TrackingProvider>

      {/* Normal contacts table has a grey background + margin all over */}
      <div
        className={classNames(
          css.filtersContainer,
          hideBuyerFilters ? undefined : css.filtersWithMargin,
        )}
      >
        <div className={css.contactFilters} aria-label="Contact Filter bar">
          <Input
            placeholder="Search job titles"
            onChange={(e) => handleTextSearch(e.target.value)}
            value={textSearch}
            className={css.searchInput}
            prefix={<SearchOutlined className={css.icon} />}
            allowClear
          />
          {showDropdownFilters && (
            <>
              <MultiSelectDropdown
                title="Function"
                searchable
                options={CATEGORY_TYPES}
                selectedOptions={contactFilters.jobFunction}
                onChange={(e) => onChangeSearchFilters({ jobFunction: e as JobFunction[] })}
              />

              <MultiSelectDropdown
                title="Seniority"
                searchable
                options={SENIORITY_OPTIONS}
                selectedOptions={contactFilters.seniority}
                onChange={(e) => onChangeSearchFilters({ seniority: e as string[] })}
              />

              {hideBuyerFilters && (
                <MultiSelectDropdown
                  title="Contact information"
                  options={CONTACTS_DISPLAY}
                  selectedOptions={contactFilters.contactDisplayOptions || []}
                  onChange={(e) => {
                    onChangeSearchFilters({ contactDisplayOptions: e });
                  }}
                />
              )}
            </>
          )}

          {showAllFiltersDrawer && (
            <Button
              buttonType="secondary"
              className={css.allFilterBtn}
              onClick={() => setFilterDrawerVisible(true)}
            >
              <FilterOutlined className={css.icon} />
              All filters {filtersAppliedCount > 0 && <Badge>{filtersAppliedCount}</Badge>}
            </Button>
          )}

          {filtersAppliedCount > 0 && (
            <TextButton bold className={css.clearFilters} onClick={handleClearFilters}>
              Clear filters
            </TextButton>
          )}
        </div>
        {resultsCount > 0 && (
          <ExportContactsButton
            filters={contactFilters}
            selectedContacts={selectedRowGuids}
            paginationState={paginationState}
          />
        )}
      </div>
    </>
  );
}
